import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { Observable, merge, tap } from 'rxjs';
import { PanelGroupItemComponent } from './panel-group-item/panel-group-item.component';

@Component({
  selector: 'panel-group',
  standalone: true,
  imports: [MatExpansionModule],
  templateUrl: './panel-group.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PanelGroupComponent),
      multi: true
    }
  ],
  host: {
    class: 'panel-group'
  }
})
export class PanelGroupComponent<T> implements AfterViewInit {
  @Input()
  value: T;

  @Input()
  disabled: boolean;

  @Output()
  change: EventEmitter<T>;

  @ContentChildren(PanelGroupItemComponent)
  panels: QueryList<PanelGroupItemComponent<unknown>>;

  @ViewChild(MatAccordion)
  accordion: MatAccordion;

  @Input()
  openAll: boolean;

  private _onChange = (value: T) => {};
  private _onTouched = () => {};
  private _changeDetector = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    this.change = new EventEmitter<T>();
  }

  ngAfterViewInit(): void {
    this.addChangeListener();

    if (this.openAll) {
      this.accordion.openAll();
    }
  }

  private addChangeListener() {
    const panelClicks$ = this.panels.map((panel) => panel.click$ as Observable<T>);

    /** we merge all the items' click events into a singular handler */
    merge(...panelClicks$)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((value: T) => {
          this.closeSiblingPanels(value);
          this.updateValueAndState(value);
        })
      )
      .subscribe();

    // this.panels.forEach((panel) => panel.panel.panel.);
  }

  /**
   * @description
   * close all sibling panels of selected panel
   * @param value the value of the current opened panel
   */
  private closeSiblingPanels<T>(value: T) {
    this.panels.map((p) => {
      if (p.context !== value) {
        p.close();
      }
    });
  }

  updateValueAndState(value: T = this.value) {
    this.writeValue(value);
    this._onTouched();
    this._onChange(value);
    this.change.emit(value);
    this._changeDetector.detectChanges();
  }

  writeValue(value: T): void {
    this.value = value;
  }
  registerOnChange(onChange: any): void {
    this._onChange = onChange;
  }
  registerOnTouched(onTouched: any): void {
    this._onTouched = onTouched;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
